<script lang="ts" setup>
import { computed, inject, ref, watch } from 'vue';
import { format } from 'date-fns';
import { type FormElement, useFormElement } from '@common';
import { LoadingCircular } from '@shared';
import { maxiNetCalendarContextKey } from '../models';

const DISPLAY_DATE_FORMAT = 'yyyy.MM.dd';
const HIDDEN_MONTH_STYLE = '.v-date-picker-month{visibility:hidden}';

const props = defineProps<{ element: FormElement }>();

const calendar = inject(maxiNetCalendarContextKey);

const { label, attributes, value: selectedRange } = useFormElement(props.element);

const isOpen = ref(false);
const visibleMonth = ref(new Date().getMonth());
const visibleYear = ref(new Date().getFullYear());

const displayValue = computed(() => {
  const range: Date[] = selectedRange.value ?? [];
  if (!range.length) return '';

  const start = format(new Date(range[0]), DISPLAY_DATE_FORMAT);
  const end = range.length > 1 ? format(new Date(range[range.length - 1]), DISPLAY_DATE_FORMAT) : null;

  return end ? `${start} – ${end}` : start;
});

/**
 * 3.7.16: (current vuetify version)
 * from version 3.9.0 there is a day slot (https://github.com/vuetifyjs/vuetify/releases?q=day&expanded=true)
 * TODO: investigate version upgrade and fix after that
 */
function buildSessionDayHighlightSelector(dates: string[]): string {
  return dates
    .map((date) => `.v-date-picker-month__day[data-v-date^="${date}"]:not(.v-date-picker-month__day--selected) .v-btn`)
    .join(',');
}

const isLoadingSessionDates = computed(() => calendar?.sessionDatesLoading.value ?? false);

const sessionDayStyles = computed(() => {
  const dates = [...(calendar?.sessionDates.value ?? [])];
  if (!dates.length) return '';

  const selector = buildSessionDayHighlightSelector(dates);

  return `${selector}{background:rgba(var(--v-theme-primary),.2)}`;
});

const pickerStyles = computed(() => (isLoadingSessionDates.value ? HIDDEN_MONTH_STYLE : sessionDayStyles.value));

watch([visibleMonth, visibleYear], ([month, year]) => calendar?.loadSessionDatesForMonth(year, month), {
  immediate: true
});
</script>

<template>
  <v-text-field
    :model-value="displayValue"
    v-bind="attributes"
    :label="label"
    append-inner-icon="mdi-calendar"
    readonly>
    <component :is="'style'">{{ pickerStyles }}</component>
    <v-menu
      v-model="isOpen"
      activator="parent"
      location="bottom right"
      :close-on-content-click="false">
      <v-sheet class="position-relative align-self-start">
        <v-date-picker
          v-model="selectedRange"
          v-model:month="visibleMonth"
          v-model:year="visibleYear"
          :disabled="isLoadingSessionDates"
          multiple="range"
          hide-header
          show-adjacent-months>
        </v-date-picker>
        <div
          v-if="isLoadingSessionDates"
          class="position-absolute top-0 bottom-0 left-0 right-0">
          <LoadingCircular></LoadingCircular>
        </div>
      </v-sheet>
    </v-menu>
  </v-text-field>
</template>
